import React, { useEffect, useState, useCallback } from 'react';

import {
	Button,
	Modal,
	Table,
	Form,
	Input,
	Select,
	Row,
	Popconfirm,
	message,
} from 'antd';
import { createCoreAxiosInstance } from 'createAxiosInstance';
import propTypes from 'prop-types';

import { useAuth } from 'hooks/useAuth';

// Action options for the form
const ACTION_CREATE = 'CREATE';
const ACTION_UPDATE = 'UPDATE';

// Action values are used directly in form field initialization

MerchantExternalIntegrations.propTypes = {
	merchantID: propTypes.string.isRequired,
	companyId: propTypes.string.isRequired,
};

export default function MerchantExternalIntegrations({
	merchantID,
	companyId,
}) {
	const [loading, setLoading] = useState(false);
	const [createLoading, setCreateLoading] = useState(false);
	const [updateLoading, setUpdateLoading] = useState(false);
	const [choices, setChoices] = useState([]);
	const { user } = useAuth();

	const [externalIntegrations, setExternalIntegrations] = useState();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [form] = Form.useForm();
	const [messageApi, contextHolder] = message.useMessage();

	const fetchExternalIntegrationData = useCallback(async () => {
		try {
			setLoading(true);
			const axios = await createCoreAxiosInstance();
			const {
				data: { choices, integrations },
			} = await axios.get(`/api/corpa/external-integration/${merchantID}/`);
			setChoices(choices.map(({ name }) => ({ value: name, label: name })));
			setExternalIntegrations(integrations);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [merchantID]);

	useEffect(() => {
		if (!merchantID) return;
		fetchExternalIntegrationData();
	}, [merchantID, fetchExternalIntegrationData]);

	const showModal = ({ create }) => {
		if (create) {
			form.setFieldsValue({
				company_id: parseInt(companyId, 10),
				merchant_cuboh_id: parseInt(merchantID, 10),
				user_id: parseInt(user?.id, 10),
				action: ACTION_CREATE,
			});
		}
		setIsModalOpen(true);
	};
	const updateOrCreateExternalIntegrationData = useCallback(
		async (values) => {
			try {
				// Set the specific loading state based on action
				if (values.action === ACTION_UPDATE) {
					setUpdateLoading(true);
				} else {
					setCreateLoading(true);
				}
				setLoading(true);

				const axios = await createCoreAxiosInstance();

				// Check the action value to determine which endpoint to use
				let response;
				if (values.action === ACTION_UPDATE) {
					// Use the update_integration custom action endpoint for UPDATE
					response = await axios.patch(
						`/api/v2/integration-onboarding/update/`,
						{
							...values,
						},
					);
				} else {
					// Use the new integration-onboarding endpoint for CREATE
					response = await axios.post(`/api/v2/integration-onboarding/`, {
						...values,
					});
				}

				// Check if the response indicates a failure despite 200 status
				if (response?.data?.status === 'failed') {
					messageApi.error(response.data.response || 'Operation failed');
				} else {
					// Success case
					await fetchExternalIntegrationData();
					messageApi.success(
						response?.data?.response || 'Operation completed successfully!',
					);
				}
			} catch (error) {
				console.error(error);
				// Extract the most useful error message
				const errorMsg =
					error.response?.data?.response ||
					error.response?.data?.message ||
					error.response?.data?.error ||
					error.message ||
					'An unknown error occurred';
				messageApi.error(`Failed: ${errorMsg}`);
			} finally {
				setLoading(false);
				setCreateLoading(false);
				setUpdateLoading(false);
			}
		},
		[fetchExternalIntegrationData, messageApi],
	);

	const handleOk = async () => {
		try {
			const values = await form.validateFields();
			// The action field determines which endpoint to use
			await updateOrCreateExternalIntegrationData(values);
			setIsModalOpen(false);
			form.resetFields();
			// Success message is handled in updateOrCreateExternalIntegrationData
		} catch (error) {
			messageApi.error(`Failed: ${error}`);
		}
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		form.resetFields();
	};

	const merchantColumns = [
		{ key: 'id', dataIndex: 'id', title: 'ID' },
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'UUID',
			dataIndex: 'uuid',
			key: 'uuid',
		},
		{
			title: 'Action',
			key: 'action',
			render: (_, record) => (
				<Popconfirm
					title="Add External integration Data?"
					description={`Add to ${record.name} - ${record.uuid}`}
					onConfirm={() => {
						form.setFieldsValue({
							company_id: companyId,
							merchant_cuboh_id: merchantID,
							user_id: user?.id,
							action: ACTION_CREATE,
						});
						showModal({ create: true });
					}}
					okText="Yes"
					cancelText="No"
				>
					<Button type="link">Add</Button>
				</Popconfirm>
			),
		},
	];

	const createExternalIntegrationMerchant = useCallback(async () => {
		try {
			setCreateLoading(true);
			setLoading(true);
			const axios = await createCoreAxiosInstance();
			const response = await axios.post(
				`/api/corpa/external-integration/${merchantID}/`,
			);

			// Check if the response indicates a failure despite 200 status
			if (response?.data?.status === 'failed') {
				messageApi.error(response.data.response || 'Operation failed');
			} else {
				// Success case
				await fetchExternalIntegrationData();
				messageApi.success(
					response?.data?.response ||
						'External integration merchant created successfully!',
				);
			}
		} catch (error) {
			console.error(error);
			// Extract the most useful error message
			const errorMsg =
				error.response?.data?.response ||
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				'An unknown error occurred';
			messageApi.error(`Failed to create: ${errorMsg}`);
		} finally {
			setLoading(false);
			setCreateLoading(false);
		}
	}, [merchantID, fetchExternalIntegrationData, messageApi]);

	const externalIntegrationMerchantDataColumns = [
		{
			title: 'External Integration Name',
			dataIndex: 'external_integration_name',
			key: 'external_integration_name',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
		},
		{
			title: 'External Location ID',
			dataIndex: 'external_location_id',
			key: 'external_location_id',
		},
		{
			title: 'Action',
			key: 'action',
			render: (_, record) => (
				<Popconfirm
					title={`Edit ${record.external_integration_name}`}
					onConfirm={() => {
						form.setFieldsValue({
							appname: record.external_integration_name,
							external_location_id: record.external_location_id,
							company_id: parseInt(companyId, 10),
							merchant_cuboh_id: parseInt(merchantID, 10),
							user_id: parseInt(user?.id, 10),
							action: ACTION_UPDATE,
							integration_id: record.id,
						});
						showModal({ create: false });
					}}
					okText="Yes"
					cancelText="No"
				>
					<Button type="link">Edit</Button>
				</Popconfirm>
			),
		},
	];

	return (
		<>
			{contextHolder}
			<Row className="mb-20">
				<Button
					type="primary"
					disabled={loading}
					loading={createLoading}
					onClick={createExternalIntegrationMerchant}
				>
					Create NEW External Integration Merchant
				</Button>
			</Row>

			<Table
				loading={loading}
				columns={merchantColumns}
				dataSource={externalIntegrations}
				rowKey={(record) => `${record.id}`}
				expandable={{
					expandedRowRender: (record) => {
						return (
							<Table
								columns={externalIntegrationMerchantDataColumns}
								dataSource={record?.integration_data ?? []}
								pagination={false}
							/>
						);
					},
				}}
				size="small"
				bordered
			/>
			<Modal
				title="External Integration"
				open={isModalOpen}
				onOk={handleOk}
				onCancel={handleCancel}
				confirmLoading={createLoading || updateLoading}
			>
				<Form form={form} layout="vertical">
					<Form.Item
						name="appname"
						label="App Name"
						rules={[
							{
								required: true,
								message: 'Please select an app name!',
							},
						]}
					>
						<Select placeholder="Select an app name" options={choices} />
					</Form.Item>

					<Form.Item
						name="external_location_id"
						label="External Location ID"
						rules={[
							{ required: true, message: 'Please input the location ID!' },
							{
								type: 'string',
								max: 200,
								message: 'Location ID must be less than 200 characters!',
							},
						]}
					>
						<Input placeholder="Enter location ID" style={{ width: '100%' }} />
					</Form.Item>

					<Form.Item name="company_id" hidden>
						<input type="hidden" />
					</Form.Item>

					<Form.Item name="merchant_cuboh_id" hidden>
						<input type="hidden" />
					</Form.Item>

					<Form.Item name="user_id" hidden>
						<input type="hidden" />
					</Form.Item>

					<Form.Item name="action" hidden>
						<input type="hidden" />
					</Form.Item>
					<Form.Item name="integration_id" hidden>
						<input type="hidden" />
					</Form.Item>

					<Form.Item name="is_external_integration" hidden initialValue={true}>
						<input type="hidden" />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
}
